import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SocHeader from '../components/SocHeader.jsx';
import Footer from '../components/Footer.jsx';
import { AI, PROVIDERS } from '../lib/ai.js';

// Model lists per provider; null = free-text input (Ollama)
const PROVIDER_KEYS = Object.keys(PROVIDERS);

function initState() {
  const cfg = AI.getConfig();
  return {
    provider:  cfg.provider  || '',
    apiKey:    cfg.apiKey    || '',
    model:     cfg.model     || '',
    ollamaUrl: cfg.ollamaUrl || 'http://localhost:11434',
  };
}

export default function AISettings() {
  const [form, setForm]           = useState(initState);
  const [testing, setTesting]     = useState(false);
  const [testOut, setTestOut]     = useState('');   // streamed test output
  const [testOk, setTestOk]       = useState(null); // true | false | null
  const [usage, setUsage]         = useState(() => AI.getUsage());
  const abortRef                  = useRef(null);

  // ---- derived ----
  const pInfo      = PROVIDERS[form.provider] || null;
  const freeModel  = pInfo?.models === null;          // Ollama
  const modelList  = pInfo?.models || [];
  const needsKey   = pInfo?.requiresKey ?? true;

  // When provider changes, reset model to provider default
  useEffect(() => {
    if (!form.provider) return;
    const def = PROVIDERS[form.provider]?.defaultModel || '';
    setForm(prev => {
      const keep = prev.model && (freeModel || modelList.includes(prev.model));
      return { ...prev, model: keep ? prev.model : def };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.provider]);

  // Auto-save config to localStorage on any form field change
  useEffect(() => {
    AI.configure({
      provider:  form.provider,
      apiKey:    form.apiKey,
      model:     form.model,
      ollamaUrl: form.ollamaUrl,
    });
  }, [form]);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  // ---- Test connection ----
  function handleTest() {
    setTesting(true);
    setTestOut('');
    setTestOk(null);

    // Abort any previous in-flight test
    if (abortRef.current) abortRef.current();
    let aborted = false;
    abortRef.current = () => { aborted = true; };

    AI.chat({
      system:   'You are a connection test. Reply with exactly one word.',
      messages: [{ role: 'user', content: 'Reply with: ONLINE' }],
      onToken: (tok) => {
        if (aborted) return;
        setTestOut(prev => prev + tok);
      },
      onDone: () => {
        if (aborted) return;
        setTesting(false);
        setTestOk(true);
        setUsage(AI.getUsage());
      },
      onError: (msg) => {
        if (aborted) return;
        setTesting(false);
        setTestOut(msg);
        setTestOk(false);
      },
    });
  }

  function handleClear() {
    if (!window.confirm('Clear all AI settings and usage data?')) return;
    AI.clearAll();
    setForm({ provider: '', apiKey: '', model: '', ollamaUrl: 'http://localhost:11434' });
    setTestOut('');
    setTestOk(null);
    setUsage({ tokens: 0, calls: 0 });
  }

  return (
    <>
      <SocHeader />
      <section className="auth-section" id="main">
        <div className="auth-card auth-card-wide">

          <div className="hero-eyebrow">AI CONFIGURATION</div>
          <h2 className="auth-headline">AI Settings</h2>

          <div className="callout" style={{ marginBottom: '2rem' }}>
            <strong>BYOK — Bring Your Own Key</strong>
            &nbsp;Your API key is stored locally in this browser only. It is never sent to
            VetTech servers — only to your chosen AI provider when you use AI features.
          </div>

          {/* ---- Provider ---- */}
          <div className="form-group">
            <label className="form-label" htmlFor="ai-provider">PROVIDER</label>
            <select
              id="ai-provider"
              className="form-input"
              value={form.provider}
              onChange={e => update('provider', e.target.value)}
            >
              <option value="">— select provider —</option>
              {PROVIDER_KEYS.map(k => (
                <option key={k} value={k}>{PROVIDERS[k].label}</option>
              ))}
            </select>
          </div>

          {/* ---- API Key (not shown for Ollama) ---- */}
          {form.provider && needsKey && (
            <div className="form-group">
              <label className="form-label" htmlFor="ai-apikey">API KEY</label>
              <input
                id="ai-apikey"
                type="password"
                className="form-input"
                placeholder="sk-••••••••"
                value={form.apiKey}
                onChange={e => update('apiKey', e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
              <span className="form-hint">Saved to localStorage. Never transmitted to VetTech.</span>
            </div>
          )}

          {/* ---- Model ---- */}
          {form.provider && (
            <div className="form-group">
              <label className="form-label" htmlFor="ai-model">MODEL</label>
              {freeModel ? (
                <input
                  id="ai-model"
                  type="text"
                  className="form-input"
                  placeholder="e.g. llama3.1"
                  value={form.model}
                  onChange={e => update('model', e.target.value)}
                />
              ) : (
                <select
                  id="ai-model"
                  className="form-input"
                  value={form.model}
                  onChange={e => update('model', e.target.value)}
                >
                  {modelList.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
            </div>
          )}

          {/* ---- Ollama URL ---- */}
          {form.provider === 'ollama' && (
            <div className="form-group">
              <label className="form-label" htmlFor="ai-ollama-url">OLLAMA URL</label>
              <input
                id="ai-ollama-url"
                type="text"
                className="form-input"
                value={form.ollamaUrl}
                onChange={e => update('ollamaUrl', e.target.value)}
                spellCheck="false"
              />
              <span className="form-hint">Default: http://localhost:11434</span>
            </div>
          )}

          {/* ---- Test connection ---- */}
          {form.provider && (
            <div className="form-group">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleTest}
                disabled={testing || (needsKey && !form.apiKey)}
                style={{ marginBottom: '0.75rem' }}
              >
                {testing ? 'TESTING…' : 'TEST CONNECTION'}
              </button>

              {(testOut || testing) && (
                <div className={`ai-test-out ${testOk === true ? 'ai-ok' : testOk === false ? 'ai-err' : ''}`}>
                  <span className="ai-test-label">
                    {testOk === true ? 'Connected' : testOk === false ? 'Error' : 'Waiting'}
                  </span>
                  <br />
                  {testOut || <span className="ai-cursor" />}
                  {testing && <span className="ai-cursor" />}
                </div>
              )}
            </div>
          )}

          {/* ---- Usage stats ---- */}
          <div className="ai-usage-bar">
            <span className="form-label">SESSION USAGE</span>
            <div className="ai-usage-grid">
              <div>
                <span className="ai-stat-val">{usage.tokens.toLocaleString()}</span>
                <span className="ai-stat-key">TOKENS</span>
              </div>
              <div>
                <span className="ai-stat-val">{usage.calls.toLocaleString()}</span>
                <span className="ai-stat-key">CALLS</span>
              </div>
            </div>
            <span className="form-hint">Cumulative across all AI features this session.</span>
          </div>

          {/* ---- Clear data ---- */}
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={handleClear}
            style={{ marginTop: '2rem', color: 'var(--crimson)', borderColor: 'var(--crimson)' }}
          >
            CLEAR ALL AI DATA
          </button>

          {/* ---- Back link ---- */}
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <Link to="/" className="form-hint" style={{ textDecoration: 'underline' }}>
              ← BACK TO HOME
            </Link>
          </div>

        </div>
      </section>
      <Footer />
    </>
  );
}
